import { Button, FormControl, FormLabel, Input, VStack, useToast } from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';
import {
  AuthHeading,
  AuthLoadingScreen,
  AuthPageLayout,
  AuthSubtext,
  PrimaryAuthButton,
} from '@/components/AuthCards/auth-ui';
import { FirebaseContext } from '@/session/FirebaseContext';
import { firebaseErrorToastOptions, isValidEmail } from '@/helpers';

type SignInPhase = 'loading' | 'needEmail' | 'redirecting' | 'failed';

export default function FinishSignInPage() {
  const ctx = useContext(FirebaseContext);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const actionHandled = useRef(false);

  const [phase, setPhase] = useState<SignInPhase>('loading');
  const [emailInput, setEmailInput] = useState('');
  const [signInUrl] = useState(() => window.location.href);

  const redirectTo = searchParams.get('redirect_to') ?? '/profile';

  const completeSignIn = async (email: string) => {
    setPhase('loading');
    try {
      await signInWithEmailLink(ctx.auth, email, signInUrl);
      localStorage.removeItem('emailForSignIn');
      await ctx.refreshAuthUser();
      setPhase('redirecting');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setPhase('failed');
      toast(firebaseErrorToastOptions('Sign in failed', err as FirebaseError));
    }
  };

  useEffect(() => {
    if (actionHandled.current) {
      return;
    }
    if (!isSignInWithEmailLink(ctx.auth, signInUrl)) {
      navigate('/login', { replace: true });
      return;
    }
    actionHandled.current = true;

    const storedEmail =
      localStorage.getItem('emailForSignIn')?.replaceAll('"', '')?.trim() ?? '';
    if (storedEmail) {
      void completeSignIn(storedEmail);
    } else {
      setPhase('needEmail');
    }
  }, [ctx.auth, navigate, signInUrl]);

  if (phase === 'loading' || phase === 'redirecting') {
    return <AuthLoadingScreen message='Signing you in…' />;
  }

  if (phase === 'failed') {
    return (
      <AuthPageLayout showLogo backTo='/login' backLabel='Back to log in'>
        <AuthHeading>Sign in failed</AuthHeading>
        <AuthSubtext mb={4}>This link is invalid or has expired. Request a new login link.</AuthSubtext>
        <PrimaryAuthButton onClick={() => navigate('/login', { replace: true })}>
          Log in
        </PrimaryAuthButton>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout showLogo backTo='/login' backLabel='Back to log in'>
      <AuthHeading>Confirm your email</AuthHeading>
      <AuthSubtext mb={4}>
        To complete sign-in, enter the email address where you received the login link.
      </AuthSubtext>
      <VStack align='stretch' spacing={4}>
        <FormControl>
          <FormLabel fontSize='sm' fontWeight='medium'>
            Email
          </FormLabel>
          <Input
            type='email'
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            autoComplete='email'
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && isValidEmail(emailInput)) {
                void completeSignIn(emailInput);
              }
            }}
          />
        </FormControl>
        <PrimaryAuthButton
          isDisabled={!isValidEmail(emailInput)}
          onClick={() => void completeSignIn(emailInput)}
        >
          Complete sign in
        </PrimaryAuthButton>
        <Button variant='ghost' onClick={() => navigate('/login', { replace: true })}>
          Back to log in
        </Button>
      </VStack>
    </AuthPageLayout>
  );
}
